import { useState, useRef, useMemo } from 'react'
import {
  useAdminDiscoverPreview,
  useAdminDiscoverImport,
  useAdminDiscoverEnrich,
  type DiscoverCandidate,
  type ImportCandidate,
} from '../../hooks/useAdmin'
import { geocodeAddress } from '../../lib/geocode'
import { ApiError } from '../../lib/api'

// ─── Types + helpers ──────────────────────────────────────────────────────────

type EditableCandidate = DiscoverCandidate & { selected: boolean }
type CandidateFilter = 'all' | 'incomplete' | 'existing'

function isComplete(c: EditableCandidate): boolean {
  return !!c.denomination && !!c.address
}

const DENOMINATION_OPTIONS = [
  { value: 'adventist', label: 'Adventist' },
  { value: 'baptist', label: 'Baptist' },
  { value: 'catholic', label: 'Catholic' },
  { value: 'methodist', label: 'Methodist' },
  { value: 'presbyterian', label: 'Presbyterian' },
  { value: 'lutheran', label: 'Lutheran' },
  { value: 'pentecostal', label: 'Pentecostal' },
  { value: 'evangelical', label: 'Evangelical' },
  { value: 'orthodox', label: 'Orthodox' },
  { value: 'anglican', label: 'Anglican' },
]

const TABLE_COLS = '36px 2.2fr 1.3fr 1.7fr 1.4fr 90px'

// ─── Candidate table ──────────────────────────────────────────────────────────

function CandidateTable({
  candidates,
  onUpdate,
  onToggle,
  onToggleAll,
  onSelectComplete,
  onSelectNone,
  onExportJSON,
  onImportJSON,
  onImport,
  onEnrich,
  onClear,
  importing,
  enrichProgress,
}: {
  candidates: EditableCandidate[]
  onUpdate: (osmId: number, field: keyof ImportCandidate, value: string | null) => void
  onToggle: (osmId: number) => void
  onToggleAll: () => void
  onSelectComplete: () => void
  onSelectNone: () => void
  onExportJSON: () => void
  onImportJSON: (e: React.ChangeEvent<HTMLInputElement>) => void
  onImport: () => void
  onEnrich: () => void
  onClear: () => void
  importing: boolean
  enrichProgress: { done: number; total: number } | null
}) {
  const [filter, setFilter] = useState<CandidateFilter>('all')
  const jsonInputRef = useRef<HTMLInputElement>(null)

  const selectedCount = candidates.filter((c) => c.selected).length
  const newborns = candidates.filter((c) => !c.alreadyExists)
  const allSelected = newborns.length > 0 && newborns.every((c) => c.selected)

  const counts = {
    all: candidates.length,
    incomplete: candidates.filter((c) => !c.alreadyExists && !isComplete(c)).length,
    existing: candidates.filter((c) => c.alreadyExists).length,
  }

  const visible = useMemo(() => {
    if (filter === 'incomplete') return candidates.filter((c) => !c.alreadyExists && !isComplete(c))
    if (filter === 'existing') return candidates.filter((c) => c.alreadyExists)
    return candidates
  }, [candidates, filter])

  const TABS: { id: CandidateFilter; label: string; amber?: boolean }[] = [
    { id: 'all', label: `All · ${counts.all}` },
    { id: 'incomplete', label: `Missing info · ${counts.incomplete}`, amber: counts.incomplete > 0 },
    { id: 'existing', label: `Already in DB · ${counts.existing}` },
  ]

  return (
    <div style={{
      background: 'var(--surface)',
      border: '1px solid var(--line-soft)',
      borderRadius: 12,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      maxHeight: 'calc(100vh - 320px)',
      minHeight: 320,
    }}>
      {/* Toolbar */}
      <div style={{
        padding: '10px 16px',
        borderBottom: '1px solid var(--line-soft)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        flexShrink: 0,
      }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', marginRight: 4 }}>
          {selectedCount} / {candidates.length} selected
        </span>
        <div style={{ width: 1, height: 18, background: 'var(--line)', margin: '0 4px' }} />
        <button onClick={onToggleAll} style={tbtn}>{allSelected ? 'Deselect all' : 'Select all'}</button>
        <button onClick={onSelectComplete} style={tbtn}>Select complete</button>
        <button onClick={onSelectNone} style={tbtn}>Select none</button>
        <div style={{ flex: 1 }} />
        <button
          onClick={onEnrich}
          disabled={!!enrichProgress || counts.incomplete === 0}
          title={counts.incomplete === 0 ? 'No incomplete rows to enrich' : `Enrich ${counts.incomplete} incomplete rows with Claude`}
          style={{
            ...tbtn,
            background: counts.incomplete > 0 ? 'linear-gradient(135deg, #7c3aed, #4f46e5)' : 'var(--surface)',
            color: counts.incomplete > 0 ? '#fff' : 'var(--muted)',
            border: counts.incomplete > 0 ? 'none' : '1px solid var(--line)',
            opacity: enrichProgress ? 0.7 : 1,
            cursor: enrichProgress || counts.incomplete === 0 ? 'not-allowed' : 'pointer',
            fontWeight: 600,
          }}
        >
          {enrichProgress
            ? `✦ Enriching ${enrichProgress.done}/${enrichProgress.total}…`
            : `✦ Enrich with AI (${counts.incomplete})`}
        </button>
        <button onClick={onExportJSON} style={tbtn}>↓ Export JSON</button>
        <button onClick={() => jsonInputRef.current?.click()} style={tbtn}>↑ Import JSON</button>
        <input ref={jsonInputRef} type="file" accept=".json" onChange={onImportJSON} style={{ display: 'none' }} />
        <button onClick={onClear} style={{ ...tbtn, color: 'var(--muted)' }}>✕ Clear</button>
        <button
          onClick={onImport}
          disabled={importing || selectedCount === 0}
          style={{
            padding: '6px 16px',
            borderRadius: 7,
            border: 'none',
            background: selectedCount > 0 ? '#22c55e' : 'var(--surface-2)',
            color: selectedCount > 0 ? '#fff' : 'var(--muted)',
            fontSize: 13,
            fontWeight: 600,
            fontFamily: 'var(--font-sans)',
            cursor: importing || selectedCount === 0 ? 'not-allowed' : 'pointer',
            opacity: importing ? 0.7 : 1,
            whiteSpace: 'nowrap',
          }}
        >
          {importing ? 'Importing…' : `Import ${selectedCount} selected`}
        </button>
      </div>

      {/* Filter tabs */}
      <div style={{
        padding: '6px 16px',
        borderBottom: '1px solid var(--line-soft)',
        display: 'flex',
        gap: 4,
        flexShrink: 0,
        background: 'var(--surface-2)',
      }}>
        {TABS.map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            style={{
              padding: '4px 10px',
              borderRadius: 6,
              border: 'none',
              background: filter === t.id ? 'var(--surface)' : 'transparent',
              color: filter === t.id ? 'var(--ink)' : t.amber ? '#b45309' : 'var(--ink-2)',
              fontWeight: filter === t.id ? 600 : 500,
              fontSize: 12.5,
              cursor: 'pointer',
              fontFamily: 'var(--font-sans)',
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Table header */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: TABLE_COLS,
        padding: '7px 16px',
        gap: 10,
        background: 'var(--surface-2)',
        borderBottom: '1px solid var(--line-soft)',
        fontSize: 11,
        fontWeight: 700,
        color: 'var(--muted)',
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        flexShrink: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <input
            type="checkbox"
            checked={allSelected}
            onChange={onToggleAll}
            style={{ cursor: 'pointer' }}
          />
        </div>
        <div>Name</div>
        <div>Denomination</div>
        <div>Address</div>
        <div>Website</div>
        <div>Status</div>
      </div>

      {/* Rows */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {visible.length === 0 && (
          <div style={{ padding: '32px 16px', textAlign: 'center', color: 'var(--muted)', fontSize: 14 }}>
            No results match this filter.
          </div>
        )}
        {visible.map((c) => {
          const missing = !c.alreadyExists && !isComplete(c)
          return (
            <div
              key={c.osmId}
              style={{
                display: 'grid',
                gridTemplateColumns: TABLE_COLS,
                padding: '7px 16px',
                borderBottom: '1px solid var(--line-soft)',
                gap: 10,
                alignItems: 'center',
                background: c.alreadyExists ? 'var(--surface-2)' : c.selected ? 'var(--surface)' : 'transparent',
                opacity: c.alreadyExists ? 0.6 : 1,
              }}
            >
              <input
                type="checkbox"
                checked={c.selected}
                disabled={c.alreadyExists}
                onChange={() => onToggle(c.osmId)}
                style={{ cursor: c.alreadyExists ? 'not-allowed' : 'pointer' }}
              />
              <input
                type="text"
                value={c.name}
                disabled={c.alreadyExists}
                onChange={(e) => onUpdate(c.osmId, 'name', e.target.value)}
                style={field(false, c.alreadyExists)}
              />
              <input
                type="text"
                value={c.denomination ?? ''}
                disabled={c.alreadyExists}
                onChange={(e) => onUpdate(c.osmId, 'denomination', e.target.value || null)}
                placeholder="—"
                style={field(!c.alreadyExists && !c.denomination, c.alreadyExists)}
              />
              <input
                type="text"
                value={c.address ?? ''}
                disabled={c.alreadyExists}
                onChange={(e) => onUpdate(c.osmId, 'address', e.target.value || null)}
                placeholder="—"
                style={field(!c.alreadyExists && !c.address, c.alreadyExists)}
              />
              <div style={{ fontSize: 11.5, color: 'var(--muted)', fontFamily: 'monospace', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {c.websiteUrl ?? '—'}
              </div>
              <div>
                {c.alreadyExists
                  ? <span style={pill('#fef3c7', '#b45309')}>In DB</span>
                  : missing
                    ? <span style={pill('#fef3c7', '#b45309')}>Incomplete</span>
                    : <span style={pill('#dcfce7', '#15803d')}>Ready</span>}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

const tbtn: React.CSSProperties = {
  padding: '5px 10px',
  borderRadius: 6,
  border: '1px solid var(--line)',
  background: 'var(--surface)',
  fontSize: 12,
  fontFamily: 'var(--font-sans)',
  cursor: 'pointer',
  color: 'var(--ink-2)',
  whiteSpace: 'nowrap',
}

function field(highlight: boolean, disabled: boolean): React.CSSProperties {
  return {
    width: '100%',
    fontSize: 13,
    fontFamily: 'var(--font-sans)',
    border: 'none',
    borderBottom: highlight ? '1.5px solid #f59e0b' : '1px solid transparent',
    background: highlight ? '#fffbeb' : 'transparent',
    color: disabled ? 'var(--muted)' : 'var(--ink)',
    outline: 'none',
    padding: '2px 4px',
    borderRadius: highlight ? '4px 4px 0 0' : 0,
    cursor: disabled ? 'default' : 'text',
  }
}

function pill(bg: string, color: string): React.CSSProperties {
  return {
    fontSize: 10.5,
    fontWeight: 700,
    padding: '2px 7px',
    borderRadius: 5,
    background: bg,
    color,
    letterSpacing: '0.04em',
    whiteSpace: 'nowrap',
    display: 'inline-block',
  }
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function AdminDiscoverPage() {
  const [cityQuery, setCityQuery] = useState('')
  const [radius, setRadius] = useState(50)
  const [geocoding, setGeocoding] = useState(false)
  const [discoverCoords, setDiscoverCoords] = useState<{ lat: number; lng: number } | null>(null)
  const [selectedDenominations, setSelectedDenominations] = useState<string[]>([])
  const [candidates, setCandidates] = useState<EditableCandidate[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [importResult, setImportResult] = useState<{ imported: number } | null>(null)
  const [enrichProgress, setEnrichProgress] = useState<{ done: number; total: number } | null>(null)

  const preview = useAdminDiscoverPreview()
  const importMutation = useAdminDiscoverImport()
  const enrichMutation = useAdminDiscoverEnrich()
  const jsonInputRef = useRef<HTMLInputElement>(null)

  async function handleGeocode() {
    if (!cityQuery.trim()) return
    setGeocoding(true)
    setError(null)
    const coords = await geocodeAddress(cityQuery.trim())
    setGeocoding(false)
    if (!coords) { setError('Could not find that location.'); return }
    setDiscoverCoords(coords)
  }

  function handlePreview() {
    if (!discoverCoords) return
    setError(null)
    setImportResult(null)
    preview.mutate(
      { lat: discoverCoords.lat, lng: discoverCoords.lng, radiusKm: radius, denominations: selectedDenominations },
      {
        onSuccess: (res) => {
          setCandidates(res.data.map((c) => ({
            ...c,
            selected: !c.alreadyExists && !!c.denomination && !!c.address,
          })))
        },
        onError: (err) => setError(err instanceof ApiError ? err.message : 'Preview failed'),
      },
    )
  }

  function handleLoadJSON() {
    jsonInputRef.current?.click()
  }

  function handleJSONFile(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0]
    if (!file) return
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const parsed = JSON.parse(ev.target?.result as string)
        if (!Array.isArray(parsed)) throw new Error('Expected an array')
        const loaded: EditableCandidate[] = (parsed as ImportCandidate[]).map((item, i) => ({
          osmId: -(i + 1),
          osmType: 'node',
          name: item.name ?? '',
          denomination: item.denomination ?? null,
          address: item.address ?? null,
          lat: item.lat,
          lng: item.lng,
          phone: item.phone ?? null,
          websiteUrl: item.websiteUrl ?? null,
          alreadyExists: false,
          selected: !!item.denomination && !!item.address,
        }))
        setCandidates(loaded)
        setImportResult(null)
        setError(null)
      } catch {
        setError('Invalid JSON — expected an array of church objects.')
      }
    }
    reader.readAsText(file)
    e.target.value = ''
  }

  function updateCandidate(osmId: number, field: keyof ImportCandidate, value: string | null) {
    setCandidates((prev) => prev?.map((c) => c.osmId === osmId ? { ...c, [field]: value } : c) ?? null)
  }

  function toggleCandidate(osmId: number) {
    setCandidates((prev) => prev?.map((c) => c.osmId === osmId ? { ...c, selected: !c.selected } : c) ?? null)
  }

  function toggleAll() {
    if (!candidates) return
    const newborns = candidates.filter((c) => !c.alreadyExists)
    const allOn = newborns.every((c) => c.selected)
    setCandidates((prev) => prev?.map((c) => c.alreadyExists ? c : { ...c, selected: !allOn }) ?? null)
  }

  function selectComplete() {
    setCandidates((prev) => prev?.map((c) => c.alreadyExists ? c : { ...c, selected: isComplete(c) }) ?? null)
  }

  function selectNone() {
    setCandidates((prev) => prev?.map((c) => ({ ...c, selected: false })) ?? null)
  }

  function handleExportJSON() {
    if (!candidates) return
    const data = candidates.map((c) => ({
      name: c.name,
      denomination: c.denomination,
      address: c.address,
      lat: c.lat,
      lng: c.lng,
      phone: c.phone,
      websiteUrl: c.websiteUrl,
    }))
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = 'churches-discovery.json'
    a.click()
    URL.revokeObjectURL(url)
  }

  function handleImportCandidates(eJson: React.ChangeEvent<HTMLInputElement>) {
    handleJSONFile(eJson)
  }

  function handleImport() {
    if (!candidates) return
    const selected = candidates.filter((c) => c.selected).map<ImportCandidate>((c) => ({
      name: c.name,
      denomination: c.denomination,
      address: c.address,
      lat: c.lat,
      lng: c.lng,
      phone: c.phone,
      websiteUrl: c.websiteUrl,
    }))
    importMutation.mutate(selected, {
      onSuccess: (res) => {
        setImportResult({ imported: res.data.imported })
        setCandidates(null)
      },
      onError: (err) => setError(err instanceof ApiError ? err.message : 'Import failed'),
    })
  }

  async function handleEnrich() {
    if (!candidates) return
    const incomplete = candidates.filter((c) => !c.alreadyExists && !isComplete(c))
    if (incomplete.length === 0) return
    setError(null)
    setEnrichProgress({ done: 0, total: incomplete.length })

    const BATCH_SIZE = 15
    let batchErrors = 0
    // Accumulate all enriched results keyed by name — applied via functional setState after each batch
    const enriched = new Map<string, { denomination: string | null; address: string | null }>()

    for (let i = 0; i < incomplete.length; i += BATCH_SIZE) {
      const batch = incomplete.slice(i, i + BATCH_SIZE)
      try {
        const res = await enrichMutation.mutateAsync(
          batch.map((c) => ({ name: c.name, denomination: c.denomination, address: c.address, lat: c.lat, lng: c.lng })),
        )
        batch.forEach((c, idx) => {
          const result = res.data[idx]
          if (result) enriched.set(c.name, { denomination: result.denomination, address: result.address })
        })
        // Snapshot current enriched map for the closure — functional update sees latest state
        const snapshot = new Map(enriched)
        setCandidates((prev) =>
          prev?.map((c) => {
            const e = snapshot.get(c.name)
            if (!e) return c
            return {
              ...c,
              denomination: e.denomination ?? c.denomination,
              address: e.address ?? c.address,
              selected: !c.alreadyExists && !!(e.denomination ?? c.denomination) && !!(e.address ?? c.address),
            }
          }) ?? null,
        )
      } catch {
        batchErrors++
      }
      setEnrichProgress({ done: Math.min(i + BATCH_SIZE, incomplete.length), total: incomplete.length })
    }

    setEnrichProgress(null)
    if (batchErrors > 0) {
      setError(`Enrichment completed with ${batchErrors} failed batch${batchErrors > 1 ? 'es' : ''}. Applied results saved — export now to preserve your work.`)
    }
  }

  function toggleDenomination(value: string) {
    setSelectedDenominations((prev) =>
      prev.includes(value) ? prev.filter((d) => d !== value) : [...prev, value],
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
        <div>
          <h1 style={{ fontSize: 27, fontWeight: 600, letterSpacing: '-0.02em', margin: '0 0 4px', color: 'var(--ink)' }}>
            Church discovery
          </h1>
          <p style={{ fontSize: 13, color: 'var(--ink-2)', margin: 0 }}>
            Scan OpenStreetMap for churches near a location, or import a JSON file directly.
            Complete rows (denomination + address) are auto-selected.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
          <input ref={jsonInputRef} type="file" accept=".json" onChange={handleJSONFile} style={{ display: 'none' }} />
          <button onClick={handleLoadJSON} style={{
            padding: '8px 14px',
            borderRadius: 8,
            border: '1px solid var(--line)',
            background: 'var(--surface)',
            fontSize: 13,
            fontWeight: 500,
            fontFamily: 'var(--font-sans)',
            cursor: 'pointer',
            color: 'var(--ink)',
          }}>
            ↑ Import JSON
          </button>
        </div>
      </div>

      {/* Scan config card */}
      <div style={{
        background: 'var(--surface)',
        border: '1px solid var(--line-soft)',
        borderRadius: 12,
        padding: '20px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: 16,
      }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          OSM scan
        </div>

        {/* Row 1: location + radius */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 6, flex: '1 1 280px', minWidth: 0 }}>
            <input
              type="text"
              value={cityQuery}
              onChange={(e) => setCityQuery(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') void handleGeocode() }}
              placeholder="City or address — e.g. Portland, OR"
              style={{
                flex: 1,
                padding: '8px 12px',
                borderRadius: 8,
                border: '1px solid var(--line)',
                background: 'var(--bg)',
                fontSize: 13,
                fontFamily: 'var(--font-sans)',
                color: 'var(--ink)',
                outline: 'none',
              }}
            />
            <button
              onClick={() => void handleGeocode()}
              disabled={geocoding || !cityQuery.trim()}
              style={{
                padding: '8px 14px',
                borderRadius: 8,
                border: '1px solid var(--line)',
                background: 'var(--surface-2)',
                fontSize: 13,
                fontFamily: 'var(--font-sans)',
                cursor: 'pointer',
                color: 'var(--ink-2)',
                whiteSpace: 'nowrap',
                opacity: geocoding || !cityQuery.trim() ? 0.5 : 1,
              }}
            >
              {geocoding ? '…' : 'Set location'}
            </button>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: '0 0 220px' }}>
            <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', whiteSpace: 'nowrap' }}>Radius</span>
            <input
              type="range"
              min={5} max={200} step={5}
              value={radius}
              onChange={(e) => setRadius(Number(e.target.value))}
              style={{ flex: 1, accentColor: 'var(--primary)' }}
            />
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink)', width: 50, textAlign: 'right' }}>
              {radius} km
            </span>
          </div>

          <button
            onClick={handlePreview}
            disabled={!discoverCoords || preview.isPending}
            style={{
              padding: '8px 20px',
              borderRadius: 8,
              border: 'none',
              background: discoverCoords ? 'var(--primary)' : 'var(--surface-2)',
              color: discoverCoords ? 'var(--on-primary, #fff)' : 'var(--muted)',
              fontSize: 13,
              fontWeight: 600,
              fontFamily: 'var(--font-sans)',
              cursor: !discoverCoords || preview.isPending ? 'not-allowed' : 'pointer',
              opacity: preview.isPending ? 0.7 : 1,
              whiteSpace: 'nowrap',
            }}
          >
            {preview.isPending ? 'Scanning OSM…' : discoverCoords ? `Scan ${discoverCoords.lat.toFixed(2)}, ${discoverCoords.lng.toFixed(2)}` : 'Set a location first'}
          </button>
        </div>

        {/* Row 2: denominations */}
        <div>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', marginBottom: 8 }}>
            Denominations — <span style={{ fontWeight: 400, color: 'var(--muted)' }}>leave all unchecked to discover every Christian church</span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 16px' }}>
            {DENOMINATION_OPTIONS.map(({ value, label }) => (
              <label key={value} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', userSelect: 'none', fontSize: 13, color: 'var(--ink-2)' }}>
                <input
                  type="checkbox"
                  checked={selectedDenominations.includes(value)}
                  onChange={() => toggleDenomination(value)}
                  style={{ accentColor: 'var(--primary)' }}
                />
                {label}
              </label>
            ))}
          </div>
        </div>

        {preview.isPending && (
          <div style={{ fontSize: 12.5, color: 'var(--muted)' }}>Querying OpenStreetMap — this can take up to 30s…</div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div style={{
          padding: '10px 16px',
          borderRadius: 8,
          background: 'var(--danger-soft, #fef2f2)',
          border: '1px solid var(--danger-line, #fca5a5)',
          fontSize: 13,
          color: 'var(--danger-deep, #b91c1c)',
        }}>
          {error}
        </div>
      )}

      {/* Import result */}
      {importResult && (
        <div style={{
          padding: '12px 16px',
          borderRadius: 8,
          background: '#f0fdf4',
          border: '1px solid #bbf7d0',
          fontSize: 13,
          color: '#166534',
          fontWeight: 500,
        }}>
          ✓ {importResult.imported} {importResult.imported === 1 ? 'church' : 'churches'} added to the database.
        </div>
      )}

      {/* Candidate table or empty state */}
      {candidates !== null ? (
        <CandidateTable
          candidates={candidates}
          onUpdate={updateCandidate}
          onToggle={toggleCandidate}
          onToggleAll={toggleAll}
          onSelectComplete={selectComplete}
          onSelectNone={selectNone}
          onExportJSON={handleExportJSON}
          onImportJSON={handleImportCandidates}
          onImport={handleImport}
          onEnrich={() => void handleEnrich()}
          onClear={() => setCandidates(null)}
          importing={importMutation.isPending}
          enrichProgress={enrichProgress}
        />
      ) : (
        <div style={{
          background: 'var(--surface)',
          border: '1px solid var(--line-soft)',
          borderRadius: 12,
          padding: '48px 24px',
          textAlign: 'center',
        }}>
          <div style={{ fontSize: 32, marginBottom: 12 }}>🔍</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--ink)', marginBottom: 6 }}>No candidates loaded</div>
          <div style={{ fontSize: 13, color: 'var(--muted)', maxWidth: 420, margin: '0 auto' }}>
            Set a location and click <strong>Scan</strong> to query OpenStreetMap, or click <strong>Import JSON</strong> in the top-right to load a previously exported file.
          </div>
        </div>
      )}
    </div>
  )
}
